import { usePostArea, useUpdateArea, useUpdateAvatarArea } from '@practium/features/api';
import { useToast } from '@practium/ui';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_AREA_FORM, AREA_FIELD_LABELS } from '../constants/area.constants';
import { AreaSchema } from '../schemas/area.schema';
import type { AreaFormData } from '../types';

export interface UseAreaFormOptions {
	propId: string;
	initialData?: AreaFormData;
	onSubmit: (createdId?: string) => void;
	avatarFile?: File | null;
}

export const useAreaForm = ({ propId, initialData, onSubmit, avatarFile }: UseAreaFormOptions) => {
	const toast = useToast();
	const [searchParams, setSearchParams] = useSearchParams();
	const areaId = searchParams.get('id');
	const isEditing = Boolean(areaId);

	const initialForm = initialData ? { ...DEFAULT_AREA_FORM, ...initialData } : { ...DEFAULT_AREA_FORM };
	const [form, setForm] = useState<AreaFormData>(initialForm);
	const initialFormRef = useRef(initialForm);
	const [errors, setErrors] = useState<Record<string, string>>({});

	const { mutateAsync: createArea, isPending: isCreating } = usePostArea();
	const { mutateAsync: updateArea, isPending: isUpdating } = useUpdateArea();
	const { mutateAsync: updateAvatarArea, isPending: isUpdatingAvatar } = useUpdateAvatarArea();
	const isLoading = isCreating || isUpdating || isUpdatingAvatar;

	const set = (key: keyof AreaFormData, value: string) => setForm((f) => ({ ...f, [key]: value }));

	const isDirty = () => JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

	const handleSubmit = async () => {
		const result = AreaSchema.safeParse(form);

		if (!result.success) {
			const fieldErrors: Record<string, string> = {};
			result.error.issues.forEach((issue) => {
				const path = String(issue.path[0] ?? '');
				if (path) fieldErrors[path] = issue.message;
			});
			setErrors(fieldErrors);
			const uniquePaths = [
				...new Set(result.error.issues.map((i) => String(i.path[0] ?? '')).filter(Boolean)),
			];
			const labels = uniquePaths.map((p) => AREA_FIELD_LABELS[p] ?? p).join(', ');
			toast.error(`Campos inválidos: ${labels}`);
			return;
		}

		setErrors({});
		try {
			const data = {
				name: form.name,
				culture: form.culture as 'cafeArabica' | 'cafeConilon',
				irrigation: form.irrigation as
					| 'none'
					| 'drip'
					| 'sprinkler'
					| 'microSprinkler'
					| 'pivot',
				file: avatarFile || null
			};

			if (isEditing && areaId) {
				await updateArea({ propId, areaId, data });

				if (avatarFile instanceof File) {
					try {
						await updateAvatarArea({ propId, areaId, avatar: avatarFile });
					} catch {
						toast.error('Área atualizada, mas não foi possível atualizar o avatar.');
					}
				}
				toast.success('Área atualizada com sucesso!');
				onSubmit(areaId);
				return;
			}

			const created = await createArea({ propId, data });
			toast.success('Área criada com sucesso!');

			if (created?._id) {
				const createdIdValue = created._id;
				setSearchParams((prev) => {
					const next = new URLSearchParams(prev);
					next.set('id', createdIdValue);
					return next;
				});
			}

			onSubmit(created?._id);
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Erro ao salvar área');
		}
	};

	return { form, errors, isLoading, set, handleSubmit, isDirty };
};
